import type { ErrorLike, OperationVariables } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useMutation as useBaseMutation } from '@apollo/client/react/compiled'
import { getMutationStatus } from '@common/misc'
import type { MutationResultAdditionalProps } from '@common/types'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { getErrorMessage, isJwtError } from '@sections/auth/errors'
import { useIntl } from 'react-intl'

import useNotifier from '../useNotifier'

export type MutationResultWithOpts<TData> = useBaseMutation.Result<TData> &
  MutationResultAdditionalProps

export type UseMutation<TData, TVariables extends OperationVariables = OperationVariables> = [
  useBaseMutation.MutationFunction<TData, TVariables>,
  MutationResultWithOpts<TData>,
]
export type UseMutationHook<TData, TVariables extends OperationVariables = OperationVariables> = (
  cbs: MutationHookOptions<TData, TVariables>,
) => UseMutation<TData, TVariables>

export type MutationHookOptions<
  TData,
  TVariables extends OperationVariables = OperationVariables,
> = useBaseMutation.Options<TData, TVariables>

export function useMutation<TData, TVariables extends OperationVariables = OperationVariables>(
  mutation: TypedDocumentNode<TData, TVariables>,
  { onCompleted, onError, ...opts }: MutationHookOptions<TData, TVariables>,
): UseMutation<TData, TVariables> {
  const notify = useNotifier()
  const intl = useIntl()

  // AC 4.2 modern signatures are inference-only (no explicit generics); the
  // inferred tuple is narrowed back to the wrapper's stable public type below.
  const [mutateFn, result] = useBaseMutation(mutation, {
    ...opts,
    // The global `defaultOptions.mutate.errorPolicy: 'all'` (apollo/client.ts)
    // makes GraphQL errors resolve instead of reject, which means `onError`
    // never fires and the notify path below would be dead. Re-assert the
    // pre-client-preset default here so the wrapper's error handling keeps
    // working — mirrors how makeQuery/makeLazyQuery re-apply their defaults.
    errorPolicy: 'none',
    onCompleted,
    onError: (err: ErrorLike) => {
      if (CombinedGraphQLErrors.is(err) && err.errors) {
        // Auth/JWT errors are handled centrally by `authErrorLink` →
        // `onAuthError` (logout + session-expired toast). Bail out so we
        // don't surface a duplicate generic error toast or invoke the
        // caller's `onError` for what is really a session-expiry event.
        if (err.errors.some(isJwtError)) {
          return
        }

        const isHandled = onError?.(err) ?? false

        if (!isHandled) {
          notify({
            autohide: 10_000,
            status: 'error',
            text: getErrorMessage(intl, err),
          })
        }
      } else {
        onError?.(err)
      }
    },
  })

  return [
    mutateFn as useBaseMutation.MutationFunction<TData, TVariables>,
    {
      ...result,
      status: getMutationStatus(result),
    },
  ]
}

function makeMutation<TData, TVariables extends OperationVariables = OperationVariables>(
  mutation: TypedDocumentNode<TData, TVariables>,
): UseMutationHook<TData, TVariables> {
  return (opts: MutationHookOptions<TData, TVariables>) =>
    useMutation<TData, TVariables>(mutation, opts)
}

export default makeMutation
